// src/components/quizzes/kit/BonusShell.js
//
// ONE engine for every "catch the right answers" bonus round (times tables,
// count-by, fractions, decimals, money, triangles, regions, continents…),
// in the BonusDiamonds look: intro card with a key, 3-2-1 countdown, navy
// HUD, combo meter, score pops, summary with stars.
//
// What every round now gets on top of its own answers:
//   💣 BOMBS     -5 and the combo breaks (about 1 drop in 6)
//   🌟 STARS     +15 bonus, rare
//   ⏰ CLOCKS    +3 seconds, rare
//   🔥 COMBO     5 right in a row = double points, 10 = triple
//   🔄 NEW QUESTION after a few right answers (newEvery), so it stays fresh
//   🏁 END GAME  finish early: keep the points, no star
//   faster read-aloud that starts straight away (kit/bonusVoice.js)
//
// SAME CONTRACT: the game file is still rendered by BonusRouter as
//   <Game onBonusComplete questions placeholderImage />
// and calls onBonusComplete(score) — now onBonusComplete(score, { endedEarly }).
//
// A game file describes its round:
//   <BonusShell {...props}
//     title emoji sub intro                 the start card + what's read aloud
//     rules=[{ icon, text, pts }]           its own rows in the key
//     init={(prev) => state}                a question (prev = the last one)
//     prompt={(state) => ({ big, small, say, extra })}
//     spawn={(state, onScreen) => value}    one falling answer
//     check={(state, value) => bool}        is it right (checked at the tap)
//     label={(value) => node}               what's written on it
//     onRight={(state, value) => ({ state, say, flash, clear, quiet, done })}  optional
//                                           (done = the round is finished — full credit)
//     newEvery={3} seconds={30} goodPoints={5} badPoints={1}
//     itemSize={96} shape="bubble"|"chip"|"plain" />

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { playGameSound } from "../../../services/gameSounds";
import confetti from "canvas-confetti";
import Faller from "./Faller";
import { tapConfetti } from "./tapFx";
import { sfxPool } from "../sfx";
import { bonusHush, bonusSay, bonusCanSpeak } from "./bonusVoice";
import BonusEndEarly from "./BonusEndEarly";
import "./bonusShell.css";

const TAP_SOUND_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Ftapsound1.mp3?alt=media&token=c60530e0-337c-4481-ae9c-87a64c076686";
const CHIME_SOUND_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Fbongsound1.mp3?alt=media&token=f47df164-06b3-494e-ba16-5ab54f707306";
const MUSIC_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gamemusic%2Fbonus-round1.mp3?alt=media&token=e4c606bb-90ca-4a78-9f2e-34a709084464";
const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f";
const TALK_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Ftalktextbutton.png?alt=media&token=c3a19304-b98a-43cf-9d81-c9b80000375e";
const MUTE_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fmute-button.png?alt=media&token=9b91203e-011f-4324-bbea-300d0d273fcf";
const UNMUTE_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Funmute-button.png?alt=media&token=e6afbbd2-41e5-41c1-b911-513fa41a4360";
export const PIRATE_BG =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fpirate-ship-background.png?alt=media&token=3912623d-30d2-468a-ab2e-449398d2bce2";

export const BUBBLE_COLORS = ["#ffd84d", "#7cc8f0", "#ff8a8a", "#86e09a", "#d3a2f0", "#ffb36b", "#6fe0cf", "#ff9fd0"];

// shared tuning — the specials every round gets
export const SPECIALS = {
  bomb: { chance: 0.16, points: 5 }, // 1 in 6 drops
  star: { chance: 0.05, points: 15 },
  clock: { chance: 0.035, seconds: 3 },
};
const COMBO_TIERS = [
  { at: 10, multiplier: 3, label: "TRIPLE!" },
  { at: 5, multiplier: 2, label: "DOUBLE!" },
];
const tierFor = (streak) => COMBO_TIERS.find((t) => streak >= t.at) || { multiplier: 1, label: null };
const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

/** A stacked fraction, for prompts and bubbles. */
export const Frac = ({ n, d }) => (
  <span className="bs-frac">
    <span>{n}</span>
    <span className="bs-frac-bar" />
    <span>{d}</span>
  </span>
);

const BonusShell = ({
  onBonusComplete,
  placeholderImage,
  title = "Bonus Round!",
  emoji = "💎",
  sub = "Catch the right answers!",
  intro = "",
  rules = [],
  init,
  prompt,
  spawn,
  check,
  label = (v) => String(v),
  onRight,
  newEvery = 0,
  seconds = 30,
  goodPoints = 5,
  badPoints = 1,
  spawnMs = 480,
  itemSize = 96,
  shape = "bubble",
  slowGood = 0.88,
  starsAt = [50, 120],
  caughtLabel = "Right answers",
  goodPopKind = "",
}) => {
  const [phase, setPhase] = useState("intro"); // intro | count | play | done
  const [countdown, setCountdown] = useState(null);
  const [score, setScore] = useState(0);
  const [timer, setTimer] = useState(seconds);
  const [items, setItems] = useState([]);
  const [pops, setPops] = useState([]);
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [caught, setCaught] = useState(0);
  const [flash, setFlash] = useState(null);
  const [shake, setShake] = useState(false);
  const [paused, setPaused] = useState(false);
  const [endedEarly, setEndedEarly] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [game, setGame] = useState(() => init(null));
  const [promptTick, setPromptTick] = useState(0); // bumps when the question changes (animation)

  const gameRef = useRef(game);
  const itemsRef = useRef(items);
  const scoreRef = useRef(0);
  const comboRef = useRef(0);
  const rightRef = useRef(0);
  const timerRef = useRef(seconds);
  const musicRef = useRef(null);
  const sentRef = useRef(false);
  gameRef.current = game;
  itemsRef.current = items;
  scoreRef.current = score;
  comboRef.current = combo;
  timerRef.current = timer;

  const playTap = useMemo(() => sfxPool(TAP_SOUND_URL), []);
  const playChime = useMemo(() => sfxPool(CHIME_SOUND_URL, 3), []);

  // ---- voice ----
  const say = useCallback(
    (text) =>
      bonusSay(text, {
        onStart: () => {
          setSpeaking(true);
          if (musicRef.current) musicRef.current.volume = 0.1;
        },
        onEnd: () => {
          setSpeaking(false);
          if (musicRef.current) musicRef.current.volume = 0.3;
        },
      }),
    []
  );
  const hush = () => {
    bonusHush();
    setSpeaking(false);
    if (musicRef.current) musicRef.current.volume = 0.3;
  };

  // ---- music ----
  useEffect(() => {
    const m = new Audio(MUSIC_URL);
    m.loop = true;
    m.volume = 0.3;
    m.play().catch(() => {});
    musicRef.current = m;
    return () => {
      m.pause();
      bonusHush();
    };
  }, []);

  // read the directions straight away (the child already pressed a button to
  // get here); if the browser still blocks it, the first tap starts it
  useEffect(() => {
    if (!intro) return undefined;
    let spoken = false;
    const go = () => {
      if (spoken) return;
      spoken = true;
      say(intro);
    };
    const t = setTimeout(go, 150);
    const onTap = () => {
      if (!speaking && !spoken) go();
    };
    window.addEventListener("pointerdown", onTap, { once: true, passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("pointerdown", onTap);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- flow ----
  const begin = () => {
    setPhase("count");
    setCountdown(3);
    const p = prompt?.(gameRef.current);
    if (p?.say) say(p.say);
    else hush();
  };

  useEffect(() => {
    if (countdown === null) return undefined;
    if (countdown === "GO!") {
      const t = setTimeout(() => {
        setCountdown(null);
        setPhase("play");
      }, 550);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setCountdown((c) => (c > 1 ? c - 1 : "GO!")), 700);
    return () => clearTimeout(t);
  }, [countdown]);

  const playing = phase === "play" && !paused;
  const frenzy = phase === "play" && timer <= 5 && timer > 0;

  // spawner
  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => {
      const r = Math.random();
      const g = gameRef.current;
      let item;
      if (r < SPECIALS.bomb.chance) item = { kind: "bomb", speed: 5.2 + Math.random() * 2 };
      else if (r < SPECIALS.bomb.chance + SPECIALS.star.chance) item = { kind: "star", speed: 3.6 + Math.random() * 1.2 };
      else if (r < SPECIALS.bomb.chance + SPECIALS.star.chance + SPECIALS.clock.chance && timerRef.current < seconds - 4) item = { kind: "clock", speed: 4.4 + Math.random() };
      else {
        const onScreen = itemsRef.current.filter((x) => x.kind === "answer");
        const value = spawn(g, onScreen);
        const good = check(g, value);
        const base = 6 + Math.random() * 3.5;
        item = { kind: "answer", value, good, speed: good ? base * slowGood : base, color: BUBBLE_COLORS[Math.floor(Math.random() * BUBBLE_COLORS.length)] };
      }
      const size = item.kind === "answer" ? itemSize : 84;
      setItems((prev) => [
        ...prev,
        { ...item, id: uid(), size, left: 5 + Math.random() * 82, drift: (Math.random() - 0.5) * 36, spin: item.kind === "answer" ? (Math.random() - 0.5) * 24 : Math.random() > 0.5 ? 300 : -300 },
      ]);
    }, frenzy ? Math.round(spawnMs * 0.62) : spawnMs);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, frenzy]);

  // clock
  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => {
      setTimer((s) => {
        if (s <= 1) {
          clearInterval(t);
          setPhase("done");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [playing]);

  // finished: celebrate, then hand the score back (or sooner with Continue)
  const send = useCallback(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    hush();
    onBonusComplete?.(Math.max(0, scoreRef.current), { endedEarly });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBonusComplete, endedEarly]);

  useEffect(() => {
    if (phase !== "done") return undefined;
    setItems([]);
    if (scoreRef.current > 0 && !endedEarly) confetti({ spread: 100, particleCount: 120, startVelocity: 50, origin: { y: 0.5 } });
    const t = setTimeout(send, endedEarly ? 4200 : 3600);
    return () => clearTimeout(t);
  }, [phase, endedEarly, send]);

  const endEarly = () => {
    setEndedEarly(true);
    setPhase("done");
  };

  // ---- tapping ----
  const removeItem = useCallback((id) => setItems((prev) => prev.filter((x) => x.id !== id)), []);
  // stable handlers for Faller (it keeps the first function it's given)
  const tapRef = useRef(null);
  const onItemTap = useCallback((item, e) => tapRef.current?.(item, e), []);
  const onItemGone = useCallback((item) => removeItem(item.id), [removeItem]);
  const addPop = (text, left, kind = "") => {
    const id = uid();
    setPops((p) => [...p, { id, text, left, kind }]);
    setTimeout(() => setPops((p) => p.filter((x) => x.id !== id)), 900);
  };
  const bump = (ms = 380) => {
    setShake(true);
    setTimeout(() => setShake(false), ms);
  };
  const showFlash = (text, small = false) => {
    setFlash({ text, small, id: uid() });
    setTimeout(() => setFlash(null), 1100);
  };
  const addScore = (n) => setScore((s) => Math.max(0, s + n));

  const nextQuestion = (state, extraSay, quiet = false) => {
    setGame(state);
    gameRef.current = state;
    setPromptTick((n) => n + 1);
    setItems((prev) => prev.filter((x) => x.kind !== "answer"));
    const p = prompt?.(state);
    const words = [extraSay, quiet ? "" : p?.say].filter(Boolean).join(" ");
    if (words) say(words);
  };

  const tap = (item, e) => {
    if (phase !== "play" || paused) return;
    removeItem(item.id);
    const left = e?.clientX ? (e.clientX / window.innerWidth) * 100 : item.left;

    if (item.kind === "bomb") {
      playGameSound("bonusBomb", "bubbleBonus", playTap);
      setCombo(0);
      bump();
      addScore(-SPECIALS.bomb.points);
      addPop(`💥 -${SPECIALS.bomb.points}`, left, "is-bad");
      return;
    }
    if (item.kind === "star") {
      playGameSound("bonusPower", "bubbleBonus", playChime);
      addScore(SPECIALS.star.points);
      addPop(`🌟 +${SPECIALS.star.points}`, left, "is-star");
      tapConfetti({ spread: 70, scalar: 0.9, startVelocity: 40, particleCount: 45, colors: ["#ffd43b", "#fff3bf", "#f59f00"], origin: { x: left / 100, y: 0.4 } });
      return;
    }
    if (item.kind === "clock") {
      playGameSound("bonusPower", "bubbleBonus", playChime);
      setTimer((s) => s + SPECIALS.clock.seconds);
      addPop(`⏰ +${SPECIALS.clock.seconds}s`, left, "is-clock");
      return;
    }

    const g = gameRef.current;
    if (check(g, item.value)) {
      const streak = comboRef.current + 1;
      const tier = tierFor(streak);
      const prevTier = tierFor(comboRef.current);
      const pts = goodPoints * tier.multiplier;
      setCombo(streak);
      setBestCombo((b) => Math.max(b, streak));
      setCaught((c) => c + 1);
      addScore(pts);
      addPop(`+${pts}`, left, goodPopKind);
      if (tier.label && tier.multiplier > prevTier.multiplier) {
        showFlash(tier.label);
        playGameSound("bonusPower", "bubbleBonus", playChime);
      } else playGameSound("bonusGood", "bubbleBonus", playTap);
      tapConfetti({ spread: 55, scalar: 0.7, startVelocity: 32, particleCount: 22, origin: { x: left / 100, y: 0.45 } });
      rightRef.current += 1;

      const res = onRight?.(g, item.value) || null;
      if (res?.done) {
        if (res.say) say(res.say);
        if (res.flash) showFlash(res.flash, true);
        setTimeout(() => setPhase("done"), 700);
        return;
      }
      if (res?.state) {
        if (res.flash) showFlash(res.flash, true);
        if (res.clear) nextQuestion(res.state, res.say, res.quiet);
        else {
          setGame(res.state);
          gameRef.current = res.state;
          if (res.say) say(res.say);
        }
        return;
      }
      if (newEvery && rightRef.current % newEvery === 0) {
        showFlash("New question!", true);
        setTimeout(() => nextQuestion(init(gameRef.current)), 350);
      }
    } else {
      playGameSound("bonusWrong", "bubbleBonus", playTap);
      setCombo(0);
      bump(260);
      addScore(-badPoints);
      addPop(`-${badPoints}`, left, "is-bad");
    }
  };

  tapRef.current = tap;

  // ---- drawing ----
  const p = prompt?.(game) || null;
  const tier = tierFor(combo);
  const stars = endedEarly ? 0 : score >= starsAt[1] ? 3 : score >= starsAt[0] ? 2 : score > 0 ? 1 : 0;
  const bg = placeholderImage || PIRATE_BG;

  const keyRows = [
    ...rules,
    { icon: "💣", text: "Bomb — don't tap it!", pts: `-${SPECIALS.bomb.points}`, neg: true },
    { icon: "🌟", text: "Bonus star", pts: `+${SPECIALS.star.points}` },
    { icon: "⏰", text: "Clock — more time", pts: `+${SPECIALS.clock.seconds}s` },
    { icon: "🔥", text: "5 in a row = double points, 10 = triple!" },
  ];

  return (
    <>
      {phase === "intro" && (
        <div className="bs-overlay">
          <div className="bs-panel">
            <div className="bs-title">
              {emoji} {title}
            </div>
            <div className="bs-sub">{sub}</div>
            <div className="bs-key">
              {keyRows.map((r) => (
                <div className="bs-key-row" key={r.text}>
                  <span className="bs-sw">{r.icon}</span>
                  <span>{r.text}</span>
                  {r.pts && <span className={`bs-pts ${r.neg || String(r.pts).startsWith("-") ? "is-neg" : ""}`}>{r.pts}</span>}
                </div>
              ))}
            </div>
            <div className="bs-actions">
              {bonusCanSpeak() && intro && (
                <button type="button" className="bs-talk" onClick={() => say(intro)} title="Read the directions" aria-label="Read the directions out loud">
                  <img src={TALK_URL} alt="Talk text" />
                </button>
              )}
              {speaking && (
                <button type="button" className="bs-stop" onClick={hush}>
                  ⏹ Stop
                </button>
              )}
              <button type="button" className="bs-start" onClick={begin}>
                Start!
              </button>
            </div>
          </div>
        </div>
      )}

      {countdown !== null && (
        <div className="bs-count">
          <span key={String(countdown)}>{countdown}</span>
        </div>
      )}

      <div className={`bs-root ${shake ? "is-shake" : ""} ${p ? "" : "no-prompt"}`} style={{ backgroundImage: `url('${bg}')` }}>
        {!p && <img src={LOGO_URL} alt="SBL Logo" className="bs-logo" />}

        <div className="bs-hud">
          <div className="bs-card">
            <span className="bs-label">Score</span>
            <div className="bs-value">{score}</div>
          </div>
          <div className="bs-card" style={{ textAlign: "right" }}>
            <span className="bs-label">Time</span>
            <div className={`bs-value ${frenzy ? "is-urgent" : ""}`}>{timer}s</div>
            <div className="bs-track">
              <div className={`bs-fill ${frenzy ? "is-urgent" : ""}`} style={{ width: `${Math.min(100, (timer / seconds) * 100)}%` }} />
            </div>
          </div>
        </div>

        {p && phase !== "intro" && (
          <div key={promptTick} className={`bs-prompt ${promptTick ? "is-new" : ""}`} aria-live="polite">
            <div className="bs-prompt-big">{p.big}</div>
            {p.small && <div className="bs-prompt-small">{p.small}</div>}
            {p.extra && <div className="bs-prompt-extra">{p.extra}</div>}
          </div>
        )}

        {phase === "play" && combo > 0 && (
          <div className="bs-combo">
            <div className="bs-combo-track">
              <div className="bs-combo-fill" style={{ width: `${Math.min(100, (combo / 10) * 100)}%` }} />
            </div>
            <div className="bs-combo-text">
              🔥 {combo} in a row
              {tier.multiplier > 1 && <span className="bs-mult">×{tier.multiplier}</span>}
            </div>
          </div>
        )}

        {flash && (
          <div key={flash.id} className={`bs-flash ${flash.small ? "is-small" : ""}`}>
            {flash.text}
          </div>
        )}

        {/* falling items — moved by the browser's compositor (Faller.js), so
            fast tapping on a phone never freezes the ones still falling */}
        {phase === "play" &&
          items.map((it) => (
            <Faller
              key={it.id}
              item={it}
              className="bs-item"
              style={{ left: `${it.left}%`, width: it.kind === "answer" && shape === "chip" ? "auto" : it.size, minWidth: it.size, height: it.size, top: -it.size - 10 }}
              speed={it.speed}
              drift={it.drift}
              spin={it.spin}
              distance="112vh"
              paused={paused}
              onDone={onItemGone}
              onTap={onItemTap}
              role="button"
              aria-label={it.kind === "answer" ? `Answer ${typeof it.value === "object" ? "" : it.value}` : it.kind}
            >
              {it.kind === "bomb" && <span className="bs-special is-bomb">💣</span>}
              {it.kind === "star" && <span className="bs-special is-star">🌟</span>}
              {it.kind === "clock" && <span className="bs-special is-clock">⏰</span>}
              {it.kind === "answer" && (
                <span
                  className={`bs-bubble ${shape === "chip" ? "is-chip" : ""} ${shape === "plain" ? "is-plain" : ""}`}
                  style={{ background: `radial-gradient(circle at 30% 26%, rgba(255,255,255,.75) 0 12%, transparent 30%), ${it.color}`, "--glow": it.color }}
                >
                  {label(it.value)}
                </span>
              )}
            </Faller>
          ))}

        {pops.map((x) => (
          <div key={x.id} className={`bs-pop ${x.kind}`} style={{ left: `${x.left}%` }}>
            {x.text}
          </div>
        ))}

        <button
          type="button"
          className="bs-mute"
          onClick={() => {
            const next = !muted;
            setMuted(next);
            if (musicRef.current) musicRef.current.muted = next;
          }}
          aria-label={muted ? "Turn music on" : "Turn music off"}
        >
          <img src={muted ? UNMUTE_URL : MUTE_URL} alt="" />
        </button>

        <div className="bs-footer">© Smart Board Learning LLC. All rights reserved.</div>
      </div>

      {(phase === "play" || phase === "count") && <BonusEndEarly onEnd={endEarly} onPause={setPaused} />}

      {phase === "done" && (
        <div className="bs-overlay is-summary">
          <div className="bs-panel">
            <div className="bs-title">{endedEarly ? "Round ended early" : "Bonus Round Complete!"}</div>
            {!endedEarly && (
              <div className="bs-stars" aria-label={`${stars} out of 3 stars`}>
                {[0, 1, 2].map((i) => (
                  <span key={i} className={`bs-star ${i < stars ? "" : "is-dim"}`}>
                    ⭐
                  </span>
                ))}
              </div>
            )}
            <div className="bs-score-label">Bonus Points</div>
            <div className="bs-score">{score}</div>
            <div className="bs-stats">
              <div className="bs-stat">
                <div className="n">{caught}</div>
                <div className="k">{caughtLabel}</div>
              </div>
              <div className="bs-stat">
                <div className="n">{bestCombo}</div>
                <div className="k">Best streak</div>
              </div>
            </div>
            {endedEarly && <p className="bs-early-note">Your points still count! Play the whole bonus round next time to earn the ⭐ star.</p>}
            <button type="button" className="bs-continue" onClick={send}>
              Continue ▶
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BonusShell;
